/**
 * Storage utility with automatic expiration
 * Based on the original vsm-web storage.js pattern
 */

interface StorageData<T> {
  val: T;
  expires: number;
}

class Storage {
  private strategy: 'localStorage' | 'sessionStorage';

  constructor(strategy: 'localStorage' | 'sessionStorage' = 'sessionStorage') {
    this.strategy = strategy;
  }

  /**
   * Set item with optional expiration
   * @param key Storage key
   * @param val Value to store
   * @param maxAge Expiration time in milliseconds (0 = no expiration)
   */
  set<T>(key: string, val: T, maxAge = 0): void {
    const data: StorageData<T> = {
      val,
      expires: maxAge === 0 ? 0 : Date.now() + maxAge,
    };
    window[this.strategy].setItem(key, JSON.stringify(data));
  }

  /**
   * Get item from storage
   * @param key Storage key
   * @returns Value or null if not found/expired
   */
  get<T>(key: string): T | null {
    const item = window[this.strategy].getItem(key);
    if (!item) return null;

    try {
      const data: StorageData<T> = JSON.parse(item);

      if (data.expires === 0) {
        return data.val;
      }

      if (Date.now() < data.expires) {
        return data.val;
      }

      // Expired, remove it
      this.remove(key);
      return null;
    } catch {
      return null;
    }
  }

  /**
   * Remove item from storage
   * @param key Storage key
   */
  remove(key: string): void {
    window[this.strategy].removeItem(key);
  }

  /**
   * Clear all items from storage
   */
  clear(): void {
    window[this.strategy].clear();
  }
}

export const sessionStorage = new Storage('sessionStorage');
export const localStorage = new Storage('localStorage');

export default {
  session: sessionStorage,
  local: localStorage,
};

